// ================= GLOBAL PIG ENGINE =================

export const pigState = {
  loaded: false,
  visible: false,

  route: "launcher",

  x: 0,
  y: 0,

  direction: "x+",

  paused: false,
  completed: false,

  shape: "rect"
};

// =====================================================
// PROCESS SIMULATION LAYER (DCS STYLE)
// =====================================================

export const processData = {

  ZT_10901: 0,
  PT_10901: 0,
  PT_10902: 0,
  TT_10901: 35,
  FIC_10902: 0,
  ZT_10902: 0,

  FIC_11003: 0,
  PT_11002: 0,
  TT_11001: 35,
  ZT_11001: 0,
  PT_11001: 0
};

export const alarmLog = [];
export const pigHistory = [];
export const timeLog = { current: null, blocks: [] };

// =====================================================
// PATH CONFIG
// =====================================================

export const PIG_PATH = {

  launcherStartX: 350,
  launcherStartY: 258,

  xv10905CheckX1: 620,
  xv10905CheckX2: 630,

  prod1BranchX1: 765,
  prod1BranchX2: 775,
  prod1DownEndY: 450,
  prod1ExitEndX: 100,

  importBranchX1: 985,
  importBranchX2: 995,
  importUpwardEndY: 105,
  importExitEndX: 1235,

  finalDropStartX: 1020,
  xv10907CheckY: 280,
  finalDownwardEndY: 382,

  receiverTransferEndX: 1235,

  receiverStartX: 1235,
  receiverStartY: 260,

  xv11002BranchX: 1020,
  receiverDownEndY: 386,
  receiverExitX: 1235,

  xv11001CheckX: 720,

  receiverFinalStopX: 350
};

// =====================================================
// SIGNALS
// =====================================================

export const pigSignals = {

  isXV10905Open: false,
  isXV10906Open: false,
  isXV10907Open: false,
  isXV10908Open: false,

  isXV11001Open: false,
  isXV11002Open: false,

  FIC_10906: 0
};

// =====================================================
// UTIL
// =====================================================

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const triggerAlarm = (msg) => {
  alarmLog.push({
    time: Date.now(),
    message: msg,
    x: pigState.x,
    y: pigState.y,
    route: pigState.route
  });
};

// =====================================================
// LOAD / REMOVE
// =====================================================

export const loadPig = (shape = "rect") => {

  pigState.loaded = true;
  pigState.visible = true;

  pigState.route = "launcher";
  pigState.paused = false;
  pigState.completed = false;

  pigState.shape = shape;

  pigState.x = PIG_PATH.launcherStartX;
  pigState.y = PIG_PATH.launcherStartY;

  pigState.direction = "x+";
};

export const removePig = () => {
  pigState.visible = false;
  pigState.loaded = false;
  pigState.completed = true;
};

// =====================================================
// SIGNAL UPDATE
// =====================================================

export const updatePigSignals = (s = {}) => {
  Object.assign(pigSignals, s);
};

// =====================================================
// PROCESS SIMULATION ENGINE
// =====================================================

const simulateProcess = (s) => {

  const speed = pigSignals.FIC_10906 / 100;

  let pressure = 40 + pigState.x / 60;

  if (!s.isXV11001Open) pressure += 15;
  if (!s.isXV11002Open) pressure += 10;
  if (pigState.paused) pressure += 25;

  processData.PT_10901 = clamp(pressure, 30, 120);
  processData.PT_11001 = clamp(pressure - 5, 30, 110);
  processData.PT_11002 = clamp(pressure - 8, 30, 100);

  let flow = 80 * speed;

  if (pigState.paused) flow *= 0.1;

  if (!s.isXV11001Open || !s.isXV11002Open) flow *= 0.6;

  processData.FIC_10902 = clamp(flow, 0, 100);
  processData.FIC_11003 = clamp(flow * 0.9, 0, 100);

  processData.TT_10901 = clamp(35 + flow / 5, 30, 80);
  processData.TT_11001 = clamp(35 + flow / 6, 30, 80);

  processData.ZT_10901 = pigState.x > 500 ? 1 : 0;
  processData.ZT_11001 = pigState.route === "receiver" ? 1 : 0;

  // history
  pigHistory.push({
    x: pigState.x,
    y: pigState.y,
    route: pigState.route,
    time: Date.now()
  });
};

// =====================================================
// ENGINE CORE
// =====================================================

export const updatePig = (s = pigSignals) => {

  const {
    isXV10905Open,
    isXV10906Open,
    isXV10907Open,
    isXV10908Open,
    isXV11001Open,
    isXV11002Open,
    FIC_10906
  } = s;

  if (!pigState.visible) return;

  if (pigState.paused && pigState.direction !== "receiver-main") {
    simulateProcess(s);
    return;
  }

  const speed = FIC_10906 / 100;
  if (speed <= 0) return;

  // =====================================================
  // LAUNCHER
  // =====================================================

  if (pigState.route === "launcher") {

    if (pigState.direction === "x+") {

      if (pigState.x >= PIG_PATH.xv10905CheckX1 && !isXV10905Open) {
        triggerAlarm("XV10905 BLOCK");
        return;
      }

      if (pigState.x >= PIG_PATH.finalDropStartX) {
        pigState.direction = "final-down";
        return;
      }

      pigState.x += speed;
    }

    else if (pigState.direction === "final-down") {

      if (pigState.y >= PIG_PATH.xv10907CheckY && !isXV10907Open) {
        pigState.y = PIG_PATH.xv10907CheckY;
        triggerAlarm("XV10907 BLOCK");
        return;
      }

      pigState.y += speed;

      if (pigState.y >= PIG_PATH.finalDownwardEndY) {
        pigState.route = "receiver";
        pigState.x = PIG_PATH.receiverStartX;
        pigState.y = PIG_PATH.receiverStartY;
        pigState.direction = "receiver-x";
      }
    }
  }

  // =====================================================
  // RECEIVER
  // =====================================================

  else if (pigState.route === "receiver") {

    if (pigState.direction === "receiver-x") {

      pigState.x -= speed;

      if (pigState.x <= PIG_PATH.xv11002BranchX) {

        pigState.x = PIG_PATH.xv11002BranchX;

        pigState.direction = isXV11002Open
          ? "receiver-down"
          : "receiver-main";

        return;
      }
    }

    else if (pigState.direction === "receiver-down") {

      pigState.y += speed;

      if (pigState.y >= PIG_PATH.receiverDownEndY) {
        pigState.y = PIG_PATH.receiverDownEndY;
        pigState.direction = "receiver-exit";
      }
    }

    else if (pigState.direction === "receiver-exit") {

      pigState.x += speed;

      if (pigState.x >= PIG_PATH.receiverExitX) {
        pigState.visible = false;
        pigState.completed = true;
      }
    }

    else if (pigState.direction === "receiver-main") {

      pigState.x -= speed;

      // CRITICAL FIX: ALWAYS RECHECK VALVE
      if (pigState.x <= PIG_PATH.xv11001CheckX) {

        pigState.x = PIG_PATH.xv11001CheckX;

        if (!isXV11001Open) {
          pigState.paused = true;
          triggerAlarm("PIG BLOCKED XV11001");
          return;
        }

        pigState.paused = false;
        pigState.direction = "receiver-final";
        return;
      }

      if (pigState.x <= PIG_PATH.receiverFinalStopX) {
        pigState.x = PIG_PATH.receiverFinalStopX;
        pigState.paused = true;
      }
    }

    else if (pigState.direction === "receiver-final") {

      pigState.x -= speed;

      if (pigState.x <= PIG_PATH.receiverFinalStopX) {
        pigState.x = PIG_PATH.receiverFinalStopX;
        pigState.paused = true;
      }
    }
  }

  simulateProcess(s);
};

// =====================================================
// ENGINE LOOP
// =====================================================

let started = false;

export const startPigEngine = () => {
  if (started) return;
  started = true;

  setInterval(() => updatePig(), 30);
};

startPigEngine();